import React, { useEffect, useState } from "react";
import { Menu, X, ArrowRight, ChevronDown, Banknote, Link2, Briefcase, ShieldCheck } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { NAV_LINKS, ASSETS } from "@/data/content";
import { goToAnchor } from "@/lib/nav";

const ICONS = { Banknote, Link2, Briefcase, ShieldCheck };

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [mobileWhy, setMobileWhy] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeAll = () => {
    setOpen(false);
    setWhyOpen(false);
    setMobileWhy(false);
  };

  const handleLink = (link) => {
    if (link.type === "route") {
      navigate(link.to);
      closeAll();
    } else if (link.type === "anchor") {
      closeAll();
      goToAnchor(navigate, pathname, link.href);
    }
  };

  const goEarlyAccess = () => {
    closeAll();
    goToAnchor(navigate, pathname, "#early-access");
  };

  const whyItems = NAV_LINKS.find((l) => l.type === "dropdown")?.items || [];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 py-2" : "bg-transparent py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" onClick={closeAll} className="flex items-center">
          <img src={ASSETS.logo} alt="WorkPay" className="h-12 sm:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => {
            if (link.type === "dropdown") {
              return (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setWhyOpen(true)}
                  onMouseLeave={() => setWhyOpen(false)}
                >
                  <button
                    data-testid="nav-why-workpay"
                    onClick={() => setWhyOpen((v) => !v)}
                    className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#091630] transition-colors"
                  >
                    {link.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${whyOpen ? "rotate-180" : ""}`} />
                  </button>

                  {whyOpen && (
                    <div
                      data-testid="why-workpay-dropdown"
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[640px]"
                    >
                      <div className="bg-white rounded-3xl border border-slate-100 soft-shadow-lg p-5">
                        <div className="grid grid-cols-2 gap-2.5">
                          {whyItems.map((item) => {
                            const Icon = ICONS[item.icon] || ShieldCheck;
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={closeAll}
                                data-testid={`why-item-${item.to.split("/").pop()}`}
                                className="group text-left flex items-start gap-3 rounded-2xl p-3.5 hover:bg-[#f7faf8] transition-colors"
                              >
                                <span className="h-10 w-10 rounded-xl bg-[#ecfdf3] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                                  <Icon className="h-5 w-5 text-[#16a34a]" />
                                </span>
                                <span>
                                  <span className="block text-sm font-bold text-[#091630] leading-tight">{item.label}</span>
                                  <span className="block text-xs text-slate-500 mt-1 leading-snug">{item.desc}</span>
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-xs text-slate-400">Project workflow by Workpay. Payments powered by Stripe.</span>
                          <Link
                            to="/why-workpay/why-vendors-choose-workpay"
                            onClick={closeAll}
                            data-testid="why-explore-cta"
                            className="flex items-center gap-1.5 text-sm font-bold text-[#16a34a] hover:gap-2.5 transition-all"
                          >
                            Explore WorkPay <ArrowRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            const cls = "text-sm font-semibold text-slate-600 hover:text-[#091630] transition-colors";
            const tid = `nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`;
            if (link.type === "route") {
              return <Link key={link.label} to={link.to} onClick={closeAll} data-testid={tid} className={cls}>{link.label}</Link>;
            }
            return (
              <a key={link.label} href={`/${link.href}`} data-testid={tid} onClick={(e) => { e.preventDefault(); handleLink(link); }} className={cls}>
                {link.label}
              </a>
            );
          })}
        </nav>

        <button
          data-testid="nav-cta-button"
          onClick={goEarlyAccess}
          className="hidden lg:flex items-center gap-2 bg-[#21c55e] text-[#091630] font-bold text-sm py-2.5 px-5 rounded-full hover:bg-[#16a34a] hover:text-white transition-colors"
        >
          Join Early Access <ArrowRight className="h-4 w-4" />
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-[#091630] p-1"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border border-slate-200 soft-shadow mt-3 mx-4 rounded-2xl p-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {NAV_LINKS.map((link) => {
            if (link.type === "dropdown") {
              return (
                <div key={link.label}>
                  <button
                    data-testid="nav-mobile-why-toggle"
                    onClick={() => setMobileWhy((v) => !v)}
                    className="w-full flex items-center justify-between py-2.5 text-slate-700 font-semibold"
                  >
                    {link.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileWhy ? "rotate-180" : ""}`} />
                  </button>
                  {mobileWhy && (
                    <div className="pl-2 pb-2 flex flex-col gap-1">
                      {whyItems.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={closeAll}
                          data-testid={`nav-mobile-why-${item.to.split("/").pop()}`}
                          className="block text-left rounded-xl p-3 hover:bg-[#f7faf8]"
                        >
                          <span className="block text-sm font-bold text-[#091630]">{item.label}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">{item.desc}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const cls = "text-left py-2.5 text-slate-700 hover:text-[#091630] font-semibold";
            if (link.type === "route") {
              return <Link key={link.label} to={link.to} onClick={closeAll} className={cls}>{link.label}</Link>;
            }
            return (
              <a key={link.label} href={`/${link.href}`} onClick={(e) => { e.preventDefault(); handleLink(link); }} className={cls}>
                {link.label}
              </a>
            );
          })}
          <button
            data-testid="nav-mobile-cta"
            onClick={goEarlyAccess}
            className="mt-2 bg-[#21c55e] text-[#091630] font-bold py-3 rounded-full flex items-center justify-center gap-2"
          >
            Join Early Access <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
